import React from 'react';
import {
    ENTITY_CRUD_INIT,
    ENTITY_CRUD_INIT_SUCCESS,
    ENTITY_CRUD_INIT_ERROR,
    ENTITY_CRUD_HIDE,
    ENTITY_CRUD_CLOSE,
    ENTITY_CRUD_OPEN,
    ENTITY_CRUD_CHANGE,
    ENTITY_CRUD_SAVE,
    ENTITY_CRUD_SAVE_LOADING,
    ENTITY_CRUD_SAVE_ERROR,
    ENTITY_CRUD_CHANGE_HAS_FILES_TO_SAVE,
    ENTITY_CRUD_TOGGLE_CONFIRM_MODAL,
    ENTITY_CRUD_INIT_WORKFLOW,
    ENTITY_DETAIL_CLOSE,
    ENTITY_CRUD_CHECK_DUPLICATE,
    ENTITY_CRUD_CHECK_DUPLICATE_SUCCESS,
    ENTITY_CRUD_CHECK_DUPLICATE_ERROR,
    ENTITY_CRUD_CHECK_DUPLICATE_RESET,
    ENTITY_CRUD_OVERLAY,
    ENTITY_CRUD_DUPLICATES_ERROR,
    ENTITY_CRUD_TOGGLE_EXTRAFIELD_MODAL,
    ENTITY_CRUD_UPDATE_TABLE_ERRORS,
    ENTITY_CRUD_SET_ACTIVE,
    ENTITY_CRUD_SET_FIELDS_TO_CLEAR,
} from 'constants/ActionTypes';
import { ACTIVITIES, COMPANIES, CONTACTS, OPPORTUNITIES } from 'constants/Entities';
import Context from 'managers/Context';
import { getFieldWorkFlow } from 'utils/fm';
import {
    getCrudFieldId,
    getSmartFieldProps,
    getValuesFromList,
    resolveSmartFieldParentClientId,
    SF_CRUD_TYPE_GENERAL,
    SF_CRUD_TYPE_INSERT,
    SF_CRUD_TYPE_UPDATE,
} from 'utils/fm/fields';
import { getEntityFromString } from 'utils/getEntityFromString';
import { ensureRoute, backRoute, backRouteWorkflow, getCurrentPath } from 'utils/routes';
import { cleanUrl } from 'utils/url';
import CheckDuplicateContent from 'containers/components/EntityCrud/components/CheckDuplicateContent';
import { CompanyAvatar, ContactAvatar } from 'containers/components/avatars';
import { trackEvent } from 'utils/tracking';
import { getActiveCrudName, getActiveCrud, getActiveLateralCrudName } from 'utils/crud';
import { isEntityDetailTabOpen } from 'utils/entityDetailTabKey';
import { EVENT_TYPES } from 'constants/Constants';
import { getLiteral } from 'utils/getLiteral';
import { isEmpty } from 'utils/isEmpty';
import {
    snapshotProductLineCellEdit,
    toCopilotModifiedProductLinesMap,
} from 'utils/copilotRegistry/copilotModifiedFields';
import { duplicatesToast, clearToast } from 'utils/toast';
import { FUZZY_ENTITIES } from 'utils/fuzzy';
import { getEntityValue } from 'services/Fuzzy';

const ENUM_IS_VISIBLE = {
    neutral: -1,
    isHidden: 0,
    isVisible: 1,
};

const getComparableFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (Array.isArray(value)) {
        if (!value.length) return null;
        return value
            .map((item) => getComparableFieldValue(item))
            .filter((item) => item !== null)
            .join('|');
    }

    if (typeof value === 'object') {
        const id = value.value ?? value.id;
        if (id === null || id === undefined || id === '' || id === '-1') return null;
        return String(id);
    }

    return String(value);
};

const hasFieldValueChanged = (oldValue, newValue) =>
    getComparableFieldValue(oldValue) !== getComparableFieldValue(newValue);

const hasMeaningfulFieldValue = (value) => getComparableFieldValue(value) !== null;

// this function is used to calculate which fields are going to be hidden or not
// because of the need to apply dynamism between extrafields, we need to make this
// function recursive to calculate the visibility of the fields affected by the
// visibility we are calculating originally. Because of that, we need 3 states of
// isVisible parameter, neutral to calculate the first level, and the other 2
// to propagate the visibility of this field to the other fields that are dependant
// on that field.
function calculateDynamicHiddenMap({
    dynamicHiddenMap,
    dynamicMap,
    key,
    value,
    data,
    isVisible = ENUM_IS_VISIBLE.neutral,
    configMap,
    smartFieldsMap,
    crudType = SF_CRUD_TYPE_GENERAL,
    visitedFields = new Set(),
}) {
    // Add current field to visited set
    const currentVisitedFields = new Set(visitedFields);
    currentVisitedFields.add(key);

    // Early exit if no dynamic mappings for this key
    const hasDynamicFieldsForKey = dynamicMap?.[key] && Object.keys(dynamicMap[key]).length > 0;
    const hasSmartFieldsForKey =
        smartFieldsMap?.[key] && Object.keys(smartFieldsMap[key]).length > 0;

    if (!hasDynamicFieldsForKey && !hasSmartFieldsForKey) {
        return dynamicHiddenMap;
    }

    // Normalize value once
    let normalizedValue = value;
    if (normalizedValue !== null && typeof normalizedValue === 'object' && normalizedValue?.value) {
        normalizedValue = normalizedValue.value;
    }

    // Handle array values with nested objects
    if (
        normalizedValue &&
        Array.isArray(normalizedValue) &&
        normalizedValue.length > 0 &&
        normalizedValue[0]?.value
    ) {
        normalizedValue = normalizedValue.map((v) => v?.value ?? v);
    }

    // Process dependent fields
    const dependentFields = [
        ...new Set([
            ...(dynamicMap?.[key] ? Object.keys(dynamicMap[key]) : []),
            ...(smartFieldsMap?.[key] ? Object.keys(smartFieldsMap[key]) : []),
        ]),
    ];

    return dependentFields.reduce((obj, dynamicField) => {
        // Initialize field object if needed
        if (!obj[dynamicField]) obj[dynamicField] = {};

        // Set visibility based on parent state
        if (isVisible === ENUM_IS_VISIBLE.isHidden) {
            obj[dynamicField] = true; // Hidden
        } else {
            const isParentVisible = obj[key] !== true;
            const hasValue =
                normalizedValue !== undefined && normalizedValue !== '' && normalizedValue !== null;
            const mappingExists = !!dynamicMap?.[key]?.[dynamicField];
            const smartMappingExists = !!smartFieldsMap?.[key]?.[dynamicField];

            // Check if field should be visible
            let shouldBeVisible = false;

            if (smartMappingExists && isParentVisible) {
                const smartFieldProperties = smartFieldsMap?.[key]?.[dynamicField];
                const matchingConfig = Array.isArray(smartFieldProperties)
                    ? smartFieldProperties.find(
                          (prop) =>
                              String(prop.value) === String(normalizedValue) &&
                              (prop.crudType === SF_CRUD_TYPE_GENERAL ||
                                  prop.crudType === crudType),
                      )
                    : null;

                // Visibility for smart fields is now driven exclusively by their explicit config.
                // If there is a matching config for the current value / crudType, we respect its
                // `visible` flag. If no matching config is found, we default to `false` (hidden)
                // instead of assuming the field is visible. This aligns with the smart field
                // initialization changes, where fields start hidden and are only shown when a
                // concrete smart-field rule marks them as visible, avoiding accidental exposure
                // of fields that lack a dedicated visibility configuration.
                shouldBeVisible = matchingConfig?.visible ?? false;
            } else if (hasValue && mappingExists && isParentVisible) {
                const mapping = dynamicMap[key][dynamicField];

                // Check if value matches any condition for visibility
                shouldBeVisible =
                    mapping === '_all_' ||
                    mapping.includes(String(normalizedValue)) ||
                    mapping.includes(normalizedValue) ||
                    (Array.isArray(normalizedValue) &&
                        Array.isArray(mapping) &&
                        mapping.some((el) => normalizedValue.includes(el)));
            }

            obj[dynamicField] = !shouldBeVisible; // true = hidden, false = visible
        }

        // Recursively process dependencies of this field
        // Skip recursive call if field is already visited (prevents infinite recursion for self-references)
        if (
            dynamicField &&
            (dynamicMap[dynamicField] || smartFieldsMap?.[dynamicField]) &&
            !currentVisitedFields.has(dynamicField)
        ) {
            const childVisibility = obj[dynamicField]
                ? ENUM_IS_VISIBLE.isHidden
                : ENUM_IS_VISIBLE.isVisible;

            calculateDynamicHiddenMap({
                dynamicHiddenMap: obj,
                dynamicMap,
                key: dynamicField,
                value: data[configMap[dynamicField]],
                data,
                isVisible: childVisibility,
                configMap,
                smartFieldsMap,
                crudType,
                visitedFields: currentVisitedFields,
            });
        }

        return obj;
    }, dynamicHiddenMap);
}

function buildDynamicHiddenMapFromData({
    dynamicMap = {},
    smartFieldsMap = {},
    data = {},
    mappedSchema,
    configMap,
    crudType,
}) {
    let dynamicHiddenMap = {};

    Object.keys(dynamicMap).forEach((key) => {
        Object.keys(dynamicMap[key]).forEach((key2) => {
            dynamicHiddenMap[key2] = true;
        });
    });

    Object.keys(smartFieldsMap).forEach((key) => {
        Object.keys(smartFieldsMap[key]).forEach((key2) => {
            dynamicHiddenMap[key2] = true;
        });
    });

    Object.keys(data).forEach((key) => {
        if (!mappedSchema?.[key]) return;

        const mapKey = mappedSchema[key].fieldConfiguration;
        if (mapKey && (dynamicMap?.[mapKey] || smartFieldsMap?.[mapKey])) {
            dynamicHiddenMap = calculateDynamicHiddenMap({
                dynamicHiddenMap,
                dynamicMap,
                key: mapKey,
                value: data[key],
                data,
                configMap,
                smartFieldsMap,
                crudType,
            });
        }
    });

    return dynamicHiddenMap;
}

export function initWorkFlow() {
    return (dispatch, getState) => {
        const state = getState();
        let workFlowPersist = sessionStorage.getItem('fm_workflow_crud_persist');
        if (workFlowPersist) {
            try {
                const workFlowFieldName = sessionStorage.getItem('fm_workflow_action_fieldName');
                workFlowPersist = JSON.parse(workFlowPersist);
                let { data, entity, hasCrudInDetail, openDetail, fieldsToUpdate } = workFlowPersist;
                dispatch(
                    init({
                        entity: entity,
                        id: data.id,
                        extraId: null,
                        isBulkAction: false,
                        isFromDetail: false,
                        data: null,
                        isFromWorkFlow: true,
                    }),
                ).then(() => {
                    let workFlowEnd = sessionStorage.getItem('fm_workflow_action_end');
                    const toSave = workFlowEnd === 'true';
                    const active = getActiveCrudName(getState());

                    if (workFlowFieldName && !toSave) {
                        data[workFlowFieldName] =
                            state[active].originalData[workFlowFieldName] || '';
                    }

                    dispatch({
                        type: ENTITY_CRUD_INIT_WORKFLOW,
                        data,
                        loading: toSave,
                        loadingSave: toSave,
                    });
                    if (toSave) {
                        dispatch(
                            save({
                                entity,
                                openDetail,
                                hasCrudInDetail,
                                fieldsToUpdate,
                                fromWorkFlow: true,
                            }),
                        );
                    } else {
                        // remove workflow word if we don't need to do anything more
                        ensureRoute(backRouteWorkflow());
                    }
                    sessionStorage.removeItem('fm_workflow_action_end');
                });
            } catch (ex) {
                console.error('something went wrong trying to restore previous crud and saving it');
            }
        }
    };
}

// Field types that render a focusable control (input, textarea, select)
// Selects/fuzzies excluded from auto-focus (dropdown UX is awkward with initial focus)
const FOCUSABLE_FIELD_TYPES = new Set([
    'text',
    'url',
    'number',
    'integer',
    'decimal',
    'currency',
    'date',
    'dateTime',
    'dateRange',
    'bool',
    'textarea',
    'richtext',
    'size',
    'checkboxGroup',
    'percent',
    'translations',
]);

const checkFirstField = (schema, data = {}) => {
    let fieldName = null;
    const tabs =
        schema?.filter((tab) =>
            tab.fields?.find(
                (field) =>
                    (!field.hidden && !field.inputs) ||
                    field.inputs?.find((input) => !input.hidden),
            ),
        ) || [];

    for (const tab of tabs) {
        const finalTab = tab.beforeRenderCrudSection ? tab.beforeRenderCrudSection(tab) : tab;
        const fields = finalTab.fields || [];

        for (const field of fields) {
            if (field.hidden || field.readOnly || field.inputs) continue;
            if (!FOCUSABLE_FIELD_TYPES.has(field.type)) continue;

            const value = data[field.id];
            const hasValue = !isEmpty(value);
            const hasDefault =
                field.defaultValue !== undefined &&
                field.defaultValue !== null &&
                field.defaultValue !== '';

            if (!hasValue && !hasDefault) {
                fieldName = field.id;
                return fieldName;
            }
        }
    }
    return fieldName;
};

export function init({
    entity,
    id,
    extraId,
    isBulkAction = false,
    isFromDetail = false,
    data,
    isFromWorkFlow = false,
    isModal = false,
    crudTab,
    extraInfo,
    parentCrudInfo,
    isFromTab = false,
    avoidRedirects = false,
    copilotModifiedFields,
    copilotProductLines,
}) {
    return (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const state = getState();
            const name = entity.entity;
            const manager = Context.entityManager.getEntitiesManager(name);
            const entityCrud = state.entityCrud;
            const active = getActiveCrudName(state);

            if (
                active &&
                entityCrud[active] &&
                entityCrud[active].id === id &&
                String(entityCrud[active].extraId ?? '') === String(extraId ?? '') &&
                id &&
                !isFromDetail &&
                !isModal
            ) {
                if (
                    !(
                        manager &&
                        manager.isEntityReadOnly &&
                        manager.isEntityReadOnly(entityCrud[active].data)
                    )
                ) {
                    dispatch({ type: ENTITY_CRUD_OPEN });
                }
                return;
            }

            dispatch({
                type: ENTITY_CRUD_INIT,
                entity: name,
                id,
                extraId,
                isBulkAction,
                isFromDetail,
                isModal,
                crudTab,
                extraInfo,
                parentCrudInfo,
                isFromTab,
                avoidRedirects,
            });

            if (!isFromWorkFlow) {
                // remove this key in this case to prevent getting wrong data
                // except is only from a current workFlow
                sessionStorage.removeItem('fm_workflow_crud_persist');
                sessionStorage.removeItem('fm_workflow_action');
                sessionStorage.removeItem('fm_workflow_action_id');
                sessionStorage.removeItem('fm_workflow_action_fieldName');
            }

            Context.entityCrudManager
                .init({
                    entity,
                    id,
                    isBulkAction,
                    isFromDetail,
                    data,
                    isModal,
                    crudTab,
                    extraInfo,
                })
                .then(
                    ({
                        schema,
                        mappedSchema,
                        configMap,
                        dependencyMap,
                        dynamicMap,
                        data,
                        preComponents,
                        smartFieldsMap,
                        smartFieldsDependencyMap,
                    }) => {
                        if (id && data && !isFromDetail) {
                            if (
                                manager &&
                                manager.isEntityReadOnly &&
                                manager.isEntityReadOnly(data)
                            ) {
                                dispatch({
                                    type: ENTITY_CRUD_CLOSE,
                                    tabs: getState().entityDetail.tabs,
                                });
                                return;
                            }
                        }

                        if (
                            manager &&
                            manager.checkIfCanOpenCrud &&
                            !manager.checkIfCanOpenCrud(data)
                        ) {
                            dispatch(close());
                            return;
                        }

                        // Set isReadOnly if no permissions
                        const crudPermission =
                            state.config?.permission?.crud_permission?.[entity.permission];
                        if (id && crudPermission && !crudPermission.update) data.isReadOnly = true;

                        // Determine crudType based on whether we're creating or updating
                        const crudType = id ? SF_CRUD_TYPE_UPDATE : SF_CRUD_TYPE_INSERT;

                        let dynamicHiddenMap = {};

                        let promises = [];

                        if (manager && manager.whileInitCrud) {
                            promises.push(manager.whileInitCrud(data, mappedSchema));
                        }
                        Promise.all(promises).then(([newData]) => {
                            if (newData) data = newData;

                            if (!id && mappedSchema && configMap) {
                                const processFieldSmartDefault = (field) => {
                                    if (!field?.smartField?.parentFieldId) return;

                                    const smartFieldProps = getSmartFieldProps({
                                        field,
                                        configMap,
                                        data,
                                        isNewEntity: true,
                                    });

                                    if (
                                        [undefined, null, ''].includes(smartFieldProps.defaultValue)
                                    ) {
                                        return;
                                    }

                                    let finalValue = smartFieldProps.defaultValue;

                                    // Keep init behavior aligned with changeFields for multi-select smart defaults.
                                    if (field.type === 'multipleValueList' && finalValue) {
                                        const listName = field.inputAttrs?.list;
                                        if (listName) {
                                            const valuesArray = getValuesFromList({
                                                list: listName,
                                                values: finalValue,
                                            });
                                            finalValue =
                                                valuesArray?.length > 0
                                                    ? valuesArray.map((item) => item.value)
                                                    : [];
                                        } else {
                                            finalValue = Array.isArray(finalValue)
                                                ? finalValue
                                                : typeof finalValue === 'string'
                                                  ? finalValue.split(';')
                                                  : [];
                                        }
                                    }

                                    data[field.id] = finalValue;
                                };

                                Object.values(mappedSchema).forEach((field) => {
                                    processFieldSmartDefault(field);
                                    if (field?.inputs?.length) {
                                        field.inputs.forEach(processFieldSmartDefault);
                                    }
                                });
                            }

                            dynamicHiddenMap = buildDynamicHiddenMapFromData({
                                dynamicMap,
                                smartFieldsMap,
                                data,
                                mappedSchema,
                                configMap,
                                crudType,
                            });

                            const firstFieldToFocus = checkFirstField(schema, data);

                            let copilotModifiedProductLines;
                            if (manager?.hasCopilotProductLines?.(data.products)) {
                                const copilotLines = data.products;
                                data = {
                                    ...data,
                                    products: manager.buildCrudLinesFromCopilotProducts(
                                        copilotLines,
                                        data,
                                        mappedSchema,
                                        getState(),
                                    ),
                                };
                                copilotModifiedProductLines = toCopilotModifiedProductLinesMap(
                                    copilotLines,
                                    data.products,
                                );
                            } else if (Array.isArray(copilotProductLines)) {
                                copilotModifiedProductLines = toCopilotModifiedProductLinesMap(
                                    copilotProductLines,
                                    data.products,
                                );
                            }

                            dispatch({
                                type: ENTITY_CRUD_INIT_SUCCESS,
                                schema,
                                mappedSchema,
                                configMap,
                                dependencyMap,
                                dynamicMap,
                                dynamicHiddenMap,
                                data,
                                firstFieldToFocus,
                                preComponents,
                                smartFieldsMap,
                                smartFieldsDependencyMap,
                                copilotModifiedFields,
                                copilotModifiedProductLines,
                            });

                            if (manager && manager.changeFieldsFromWeb3) {
                                const fieldsToChange = manager.changeFieldsFromWeb3();
                                if (fieldsToChange) {
                                    dispatch(changeFields(fieldsToChange));
                                    cleanUrl();
                                }
                            }

                            resolve();
                        });
                    },
                )
                .catch((error) => {
                    console.error(error);
                    dispatch({
                        type: ENTITY_CRUD_INIT_ERROR,
                        error,
                    });
                    dispatch(close());
                });
        });
}

// fields -> new changed fields
export function changeFields(fields, { copilotFieldIds } = {}) {
    return (dispatch, getState) => {
        return new Promise((resolve) => {
            const state = getState();
            const entityCrud = state.entityCrud;
            const active = getActiveCrudName(state);
            if (!active || !entityCrud?.[active]) return;
            const manager = Context.entityManager.getEntitiesManager(entityCrud[active].entity);
            const {
                data,
                dependencyMap,
                dynamicMap,
                mappedSchema,
                originalData,
                configMap,
                smartFieldsMap,
            } = entityCrud[active];
            const suppliedCopilotFieldIds = Array.isArray(copilotFieldIds) ? copilotFieldIds : [];
            const copilotProductsPayload =
                suppliedCopilotFieldIds.includes('products') && Array.isArray(fields.products)
                    ? fields.products
                    : null;
            const productLineCellEdit = snapshotProductLineCellEdit(fields.product, data.products);

            fields = Object.keys(fields).reduce((obj, key) => {
                if (!mappedSchema || !mappedSchema[key]) return obj;
                const mapKey = mappedSchema[key].fieldConfiguration;

                if (mapKey && dependencyMap[mapKey.toLowerCase()]) {
                    if (!hasFieldValueChanged(data[key], fields[key])) {
                        return obj;
                    }

                    dependencyMap[mapKey.toLowerCase()].forEach((son) => {
                        if (!mappedSchema?.[son]?.inputAttrs?.parentFieldNoRemove) {
                            if (
                                fields.hasOwnProperty(son) &&
                                hasMeaningfulFieldValue(fields[son])
                            ) {
                                return;
                            }

                            if (mappedSchema?.[son]?.type === 'fuzzySearchMultiple') {
                                fields[son] = [];
                            } else fields[son] = mappedSchema?.[son]?.defaultValue || '';
                        }
                    });
                }
                return obj;
            }, fields);

            // check if we have some workFlows
            const entityConstant = getEntityFromString(entityCrud[active].entity);
            const workFlows =
                entityConstant &&
                entityConstant.workFlow &&
                getState().config.workFlows &&
                getState().config.workFlows[entityConstant.workFlow];
            let workFlowAction = entityCrud[active].workFlowAction;
            if (workFlows) {
                let idUserType = getState().config.userData.idTipoUsuario;
                Object.keys(fields).map((keyField) => {
                    if (fields[keyField]) {
                        let field = mappedSchema?.[keyField];
                        if (field) {
                            const fieldWorkFlow = getFieldWorkFlow(
                                field.fieldConfiguration,
                                workFlows,
                                idUserType,
                            );

                            let oldFieldValue = entityCrud[active].data[keyField] || '-1';
                            if (oldFieldValue !== '-1' && oldFieldValue.value)
                                oldFieldValue = oldFieldValue.value;

                            const newFieldValue = fields[keyField].value
                                ? fields[keyField].value
                                : fields[keyField];

                            if (fieldWorkFlow && fieldWorkFlow[oldFieldValue]) {
                                if (oldFieldValue === '-1') {
                                    // we don't accept actions from value -1
                                    workFlowAction = null;
                                } else if (originalData[keyField] === newFieldValue) {
                                    // the user move to the original value of the field,
                                    // so we cannot dispatch any action
                                    workFlowAction = null;
                                } else {
                                    let workFlowField = fieldWorkFlow[oldFieldValue].relations.find(
                                        (field) => field.value === parseInt(newFieldValue, 10),
                                    );
                                    if (
                                        workFlowField &&
                                        workFlowField.actions &&
                                        workFlowField.actions[0]
                                    ) {
                                        // at this moment, we only accept 1 action
                                        workFlowAction = workFlowField;
                                        // add the name of the field because maybe we need it
                                        // i.e. if user cancel action and we need to recover
                                        // previous value
                                        workFlowAction.fieldName = keyField;
                                    }
                                }
                            }
                        }
                    }
                });
            }

            // Handle multi-environment field changes - clear selects and fuzzies when environment changes
            const config = Context.cacheManager.getConfigStore();
            const multiEnvironment = config?.permisos?.multiEnvironment;

            if (multiEnvironment) {
                const environmentField = Object.keys(fields).find((key) => {
                    const fieldConfig = mappedSchema[key];
                    return fieldConfig?.isMultiEnvironmentField;
                });

                if (environmentField && fields[environmentField] !== undefined) {
                    const currentEnvironmentValue = data[environmentField];
                    const newEnvironmentValue = fields[environmentField];

                    // Extract primitive values for comparison (handle both object and primitive formats)
                    const currentPrimitive =
                        currentEnvironmentValue && typeof currentEnvironmentValue === 'object'
                            ? currentEnvironmentValue.value
                            : currentEnvironmentValue;
                    const newPrimitive =
                        newEnvironmentValue && typeof newEnvironmentValue === 'object'
                            ? newEnvironmentValue.value
                            : newEnvironmentValue;

                    // If environment field changed and has a previous value
                    if (
                        (currentPrimitive || (!currentPrimitive && data?.id)) &&
                        newPrimitive !== currentPrimitive
                    ) {
                        // Clear all select and fuzzy fields
                        Object.keys(mappedSchema).forEach((fieldKey) => {
                            const fieldConfig = mappedSchema[fieldKey];
                            if (fieldConfig.isMultiEnvironmentField) return;
                            if (
                                fieldConfig?.type === 'singleValueList' ||
                                fieldConfig?.type === 'multipleValueList' ||
                                fieldConfig?.type === 'fuzzySearchSingle' ||
                                fieldConfig?.type === 'fuzzySearchMultiple' ||
                                fieldConfig?.inputAttrs?.list
                            ) {
                                Context.entityCrudManager.getDbDefaultValuesField(
                                    fields,
                                    fieldConfig,
                                );
                            }
                        });
                        if (manager && manager.resetOnEnvironmentChange) {
                            manager.resetOnEnvironmentChange({ fields, data });
                        }
                    }
                }
            }

            if (manager && manager.changeFields) fields = manager.changeFields(fields);

            const copilotModifiedProductLines = copilotProductsPayload
                ? toCopilotModifiedProductLinesMap(copilotProductsPayload, fields.products)
                : undefined;

            const fuzzyRequests = {};
            if (mappedSchema) {
                const updatedData = { ...data, ...fields };
                const isNewEntity = !data.id;
                const changedFieldIds = Object.keys(fields);

                const processFieldForSmartDefaults = (field) => {
                    const parentFieldClientId = field.smartField?.parentFieldId
                        ? resolveSmartFieldParentClientId(configMap, field.smartField.parentFieldId)
                        : undefined;
                    if (parentFieldClientId && changedFieldIds.includes(parentFieldClientId)) {
                        const smartFieldProps = getSmartFieldProps({
                            field,
                            configMap,
                            data: updatedData,
                            isNewEntity,
                        });

                        if ([undefined, null, ''].includes(smartFieldProps.defaultValue)) {
                            return;
                        }

                        let finalValue = smartFieldProps.defaultValue;

                        // Convert defaultValue to array for multipleValueList fields
                        if (field.type === 'multipleValueList' && finalValue) {
                            const listName = field.inputAttrs?.list;
                            if (listName) {
                                const valuesArray = getValuesFromList({
                                    list: listName,
                                    values: finalValue,
                                });
                                finalValue =
                                    valuesArray?.length > 0
                                        ? valuesArray.map((item) => item.value)
                                        : [];
                            } else {
                                finalValue = Array.isArray(finalValue)
                                    ? finalValue
                                    : typeof finalValue === 'string'
                                      ? finalValue.split(';')
                                      : [];
                            }
                        }

                        fields[field.id] = finalValue;
                        updatedData[field.id] = finalValue;
                    }
                };

                Object.values(mappedSchema).forEach((field) => {
                    processFieldForSmartDefaults(field);

                    if (field.inputs?.length) {
                        field.inputs.forEach(processFieldForSmartDefaults);
                    }
                });

                Object.keys(fields).forEach((fieldId) => {
                    const field = mappedSchema[fieldId];
                    if (
                        !field ||
                        (field.type !== 'fuzzySearchSingle' && field.type !== 'fuzzySearchMultiple')
                    ) {
                        return;
                    }

                    const fieldValue = fields[fieldId];
                    if (!fieldValue) return;

                    if (
                        field.type === 'fuzzySearchSingle' &&
                        typeof fieldValue === 'object' &&
                        fieldValue.label
                    ) {
                        return;
                    }

                    if (
                        field.type === 'fuzzySearchMultiple' &&
                        Array.isArray(fieldValue) &&
                        fieldValue.length > 0 &&
                        fieldValue[0]?.label
                    ) {
                        return;
                    }

                    const listName = field.inputAttrs?.list;
                    if (!listName) return;

                    const entity = FUZZY_ENTITIES[listName];
                    const entityName = entity ? entity.trueName : listName;

                    if (!fuzzyRequests[entityName]) {
                        fuzzyRequests[entityName] = [];
                    }

                    if (field.type === 'fuzzySearchSingle') {
                        const value =
                            typeof fieldValue === 'object' ? fieldValue.value : fieldValue;
                        if (value && value !== '-1') {
                            fuzzyRequests[entityName].push({
                                fieldId,
                                ids: [value],
                                type: 'single',
                            });
                        }
                    } else {
                        const ids = Array.isArray(fieldValue)
                            ? fieldValue.map((v) => (typeof v === 'object' ? v.value : v))
                            : typeof fieldValue === 'string'
                              ? fieldValue.split(';').filter(Boolean)
                              : [];

                        if (ids.length > 0) {
                            fuzzyRequests[entityName].push({
                                fieldId,
                                ids,
                                type: 'multiple',
                            });
                        }
                    }
                });
            }

            const processFuzzyLabels = () => {
                const requests = Object.entries(fuzzyRequests);
                if (requests.length === 0) return Promise.resolve();

                return Promise.all(
                    requests.map(([entityName, fuzzyFields]) => {
                        const allIds = [...new Set(fuzzyFields.flatMap((f) => f.ids))];
                        return getEntityValue(entityName, allIds)
                            .then((response) => ({
                                data: response?.data || {},
                                fields: fuzzyFields,
                            }))
                            .catch((error) => {
                                console.error(
                                    `Error fetching fuzzy values for ${entityName}:`,
                                    error,
                                );
                                return { data: {}, fields: fuzzyFields };
                            });
                    }),
                ).then((responses) => {
                    responses.forEach(({ data: entityData, fields: fuzzyFields }) => {
                        if (!entityData) return;

                        fuzzyFields.forEach(({ fieldId, ids, type }) => {
                            if (type === 'single') {
                                const value = entityData[ids[0]];
                                if (value?.description && value?.id) {
                                    fields[fieldId] = {
                                        label: value.description,
                                        value: value.id,
                                    };
                                }
                            } else {
                                fields[fieldId] = ids
                                    .map((id) => {
                                        const value = entityData[id];
                                        return value?.description && value?.id
                                            ? { label: value.description, value: value.id }
                                            : null;
                                    })
                                    .filter(Boolean);
                            }
                        });
                    });
                });
            };

            processFuzzyLabels().then(() => {
                const dynamicData = {
                    ...data,
                    ...fields,
                };

                const crudType = data.id ? SF_CRUD_TYPE_UPDATE : SF_CRUD_TYPE_INSERT;

                const dynamicHiddenMap = [
                    ...new Set([
                        ...Object.keys(dynamicMap || {}),
                        ...Object.keys(smartFieldsMap || {}),
                    ]),
                ].reduce((dynamicObj, dynamicKey) => {
                    const configKey = configMap[dynamicKey];
                    return calculateDynamicHiddenMap({
                        dynamicHiddenMap: dynamicObj,
                        dynamicMap,
                        key: dynamicKey,
                        value: dynamicData[configKey],
                        data: dynamicData,
                        configMap,
                        smartFieldsMap,
                        crudType,
                    });
                }, {});

                function dispatchChanges(
                    newFields,
                    nextCopilotFieldIds,
                    nextCopilotModifiedProductLines,
                    nextProductLineCellEdit,
                ) {
                    dispatch({
                        type: ENTITY_CRUD_CHANGE,
                        fields: newFields,
                        dynamicHiddenMap,
                        workFlowAction,
                        copilotFieldIds: nextCopilotFieldIds,
                        copilotModifiedProductLines: nextCopilotModifiedProductLines,
                        productLineCellEdit: nextProductLineCellEdit,
                    });
                }

                dispatchChanges(
                    fields,
                    suppliedCopilotFieldIds,
                    copilotModifiedProductLines,
                    productLineCellEdit,
                );
                resolve(fields);

                if (manager && manager.postChangeFields)
                    manager.postChangeFields({ fields, data, dispatchChanges, state });
            });
        });
    };
}

export function changeHasFilesToSave(bool) {
    return (dispatch, getState) => {
        dispatch({ type: ENTITY_CRUD_CHANGE_HAS_FILES_TO_SAVE, hasFilesToSave: bool || false });
    };
}

const changeFieldsAfterCreateInContext = (entityCrud, data, entity) => {
    const parentCrudInfo = entityCrud.parentCrudInfo || null;
    if (
        !data?.Insert?.id ||
        !parentCrudInfo?.id ||
        !parentCrudInfo?.fieldToChange ||
        !parentCrudInfo?.list
    )
        return;

    const fieldNameByEntity = {
        [COMPANIES.entity]: ['name'],
        [CONTACTS.entity]: ['Name', 'Surnames'],
        [OPPORTUNITIES.entity]: ['reference'],
    };

    const searchFieldName = fieldNameByEntity[entity.entity] || null;

    if (!searchFieldName) return;

    let text = '';
    if (searchFieldName) {
        text = searchFieldName.reduce((str, current, index) => {
            let newText = '';
            const searchFieldId = (current && getCrudFieldId(entity, current)) || '';
            newText = entityCrud.data[searchFieldId];
            if (newText) {
                if (index === 0) {
                    if (newText) str = newText;
                } else {
                    newText = entityCrud.data[searchFieldId];
                    if (newText) str = `${str} ${newText}`;
                }
            }
            return str;
        }, '');
    }

    if (!text || !data.Insert.id) return;
    const state = Context.store.getState();

    const lateralEntityCrudName = getActiveLateralCrudName(state);
    const lateralEntityCrud = state.entityCrud?.[lateralEntityCrudName] || null;
    const lateralData = lateralEntityCrud?.data || null;
    const oldValues = lateralData?.[parentCrudInfo.fieldToChange] || [];

    let values = null;

    if (parentCrudInfo.type === 'fuzzySearchMultiple') {
        values = [...oldValues, { label: text, value: data.Insert.id }];
    } else {
        values = { label: text, value: data.Insert.id };
    }

    const fields = {
        [parentCrudInfo.fieldToChange]: values,
    };

    // To give time to all the processes in front end ocurr correctly
    // Has no backend dependency
    setTimeout(() => {
        // Blur the active element to prevent the select from opening its menu
        // when focus returns to the parent crud after modal closes
        document.activeElement?.blur();
        Context.store.dispatch(changeFields(fields));
    }, 500);
};

export function save({
    entity,
    openDetail = true,
    hasCrudInDetail = false,
    fieldsToUpdate = {},
    fromWorkFlow = false,
    forceRefresh = false,
    info = {},
}) {
    return (dispatch, getState) =>
        new Promise((resolve, reject) => {
            dispatch({ type: ENTITY_CRUD_SAVE_LOADING, state: true });
            const entityCrud = getActiveCrud(getState());
            const duplicateControlMode = String(
                getState()?.config?.permission?.checkDuplicateFields ?? '',
            );
            const manager = Context.entityManager.getEntitiesManager(entityCrud);
            let persistCrudWorkFlow = {
                entity,
                openDetail,
                hasCrudInDetail,
                fieldsToUpdate,
                data: entityCrud.data,
            };

            let entityDetail = getState().entityDetail;
            let saveFromDetail =
                entityDetail?.[entityDetail.active]?.hasCrudInDetail &&
                hasCrudInDetail &&
                entityDetail.isOpen;

            const entityList = getState()?.entityList?.[entity.entity] || null;
            const useLazyLoad = entityList?.useLazyLoad || false;

            const isFromTab =
                entityCrud.isFromTab || Object.entries(getState().entityDetail.tabs).length > 1;

            Context.entityCrudManager
                .save({
                    entity,
                    entityState: entityCrud,
                    persistCrudWorkFlow,
                    fromDetail: !getState().entityCrud.isOpen,
                    fromWorkFlow,
                    customState: null,
                    hasFilesToSave: entityCrud.hasFilesToSave || false,
                })
                .then((data) => {
                    // Duplicate warnings should only block save in mode "2" (warning + block)
                    if (data?.Status?.code === 'DuplicatedEntity' && duplicateControlMode === '2') {
                        return resolve(data);
                    }

                    if (entityCrud.data.id) {
                        trackEvent({
                            entity,
                            entityId: entityCrud.data.id,
                            idEventType: EVENT_TYPES.update,
                        });
                    } else if (data?.Insert?.id) {
                        trackEvent({
                            entity,
                            entityId: data.Insert.id,
                            idEventType: EVENT_TYPES.create,
                        });
                    }

                    if (!saveFromDetail && !entityCrud.isModal) {
                        dispatch({ type: ENTITY_CRUD_SAVE });
                        dispatch({ type: ENTITY_CRUD_CLOSE, tabs: getState().entityDetail.tabs });
                    } else if (!entityCrud.isModal) {
                        const updatedFields = Object.keys(fieldsToUpdate).reduce((obj, key) => {
                            if (entityCrud.data[key]) {
                                obj[key] = entityCrud.data[key];
                            }
                            return obj;
                        }, {});

                        dispatch(
                            Context.actions.EntityActions.updateEntity(
                                entity,
                                entityCrud.data.id,
                                updatedFields,
                            ),
                        );
                        dispatch({ type: ENTITY_CRUD_HIDE });
                    }

                    // crud response could have two options... a normal one, returning the data
                    // and another one, backend style, returning an XML parsed as a JSON by proxy
                    let id;

                    // Hook for generic crud
                    const detailId = data?.id || entityCrud.id;
                    const activityTypeForDetail =
                        entityCrud?.extraInfo?.activityType ?? entityCrud?.extraInfo?.ActivityType;
                    if (
                        manager &&
                        manager.shouldOpenDetail &&
                        manager.shouldOpenDetail !== undefined &&
                        (!entityCrud.isModal ||
                            (entityCrud.isModal &&
                                isEntityDetailTabOpen(entityDetail.tabs, entity.entity, detailId, {
                                    activityType: activityTypeForDetail,
                                })))
                    )
                        openDetail = manager.shouldOpenDetail(data);

                    if (openDetail) {
                        if (data && data.id) {
                            // TODO: Why do we have it repeated? Refresh calls open :S
                            // TODO: seems that refresh only get the new info for detail
                            // TODO: if the detail is opened before, so we can call
                            // TODO: just open and the detail will appear
                            // dispatch(Context.actions.EntityDetailActions.refresh(entity, data.id));
                            id = data.id;
                        } else if (entityCrud.id) {
                            id = entityCrud.id;
                        } else if (data && Array.isArray(data.Insert) && data.Insert.length > 0) {
                            id = data.Insert[0].id;
                        } else if (
                            data &&
                            data.Insert &&
                            data.Insert.num &&
                            data.Insert.num === '1' &&
                            data.Insert.id
                        ) {
                            id = data.Insert.id;
                        } else if (
                            data &&
                            data.Updated &&
                            data.Updated.num &&
                            data.Updated.num === '1'
                        ) {
                            id = entityCrud.id;
                        }

                        if (id) {
                            // TODO null parameter is extra in open method. In a future
                            // TODO maybe we need to fill it with some data

                            const shouldDoHiddenRefresh =
                                getState().entityDetail.isOpen &&
                                entityCrud.entity !== ACTIVITIES.entity;

                            if (!shouldDoHiddenRefresh) {
                                const detailInfo = activityTypeForDetail
                                    ? {
                                          activityType: activityTypeForDetail,
                                          parentEntityContext:
                                              entityCrud?.extraInfo?.parentEntityContext || null,
                                          parentEntityFilter:
                                              entityCrud?.extraInfo?.parentEntityFilter || null,
                                      }
                                    : null;
                                dispatch(
                                    Context.actions.EntityDetailActions.open(
                                        entity,
                                        id,
                                        hasCrudInDetail,
                                        null,
                                        true,
                                        false,
                                        detailInfo,
                                        isFromTab,
                                        entityCrud.avoidRedirects,
                                        entityCrud?.extraInfo?.origin || null,
                                    ),
                                );

                                if (!isFromTab && !entityCrud.avoidRedirects) {
                                    const activityType = entityCrud?.extraInfo?.activityType;
                                    const isMapOpen = window.location.href.includes('/map/');
                                    const route = activityType
                                        ? `${entity.route}/${id}/${activityType}`
                                        : isMapOpen
                                          ? `${entity.route}/map/${id}`
                                          : `${entity.route}/${id}`;
                                    ensureRoute(route);
                                }
                            } else {
                                dispatch(
                                    Context.actions.EntityDetailActions.hiddenRefresh(entity, id),
                                );
                            }
                        }
                    } else if (!entityCrud.isModal && !entityCrud.avoidRedirects) {
                        if (manager && manager.backRoute) {
                            if (data?.Insert?.id) manager.backRoute(entityCrud, data?.Insert?.id);
                            else manager.backRoute(entityCrud);
                        } else ensureRoute(backRoute());
                    }

                    const activeList = getState().entityList.active || '';
                    const activeEntity = getEntityFromString(activeList);
                    // Prevents to make an unnecessary call to refresh the list when we
                    // create an entity from global create and we are not in the same menu section
                    // of the entity that we are creating
                    if (info.refreshIf?.length && activeEntity) {
                        if (!info.refreshIf.includes(activeEntity)) {
                            resolve(data);
                        }
                    }

                    // useLazyLoad for w5 table
                    // make 2 possible calls because null removes default prop values
                    if (manager?.customInitList) {
                        manager.customInitList(entityCrud.data, data?.Insert?.id);
                    } else if (useLazyLoad) {
                        dispatch(
                            Context.actions.EntityListActions.init(
                                entity,
                                false,
                                null,
                                null,
                                null,
                                useLazyLoad,
                            ),
                        );
                    } else if (
                        (!entityCrud.isModal || (entityCrud.isModal && forceRefresh)) &&
                        (!isFromTab || (isFromTab && entityCrud.entity === activeList))
                    ) {
                        let pageSize = null;
                        if (manager && manager.getCustomPageSize) {
                            pageSize = manager.getCustomPageSize();
                        }

                        dispatch(
                            Context.actions.EntityListActions.init(entity, true, pageSize || null),
                        );
                    }

                    changeFieldsAfterCreateInContext(entityCrud, data, entity);

                    resolve(data);
                })
                .catch((e) => {
                    if (e?.previewOnly) {
                        resolve();
                        return;
                    }

                    console.error(e);
                    let serverError = false;
                    if (hasCrudInDetail) {
                        const tabs = getState().entityDetail.tabs;
                        dispatch({ type: ENTITY_DETAIL_CLOSE, tabs });
                        const wasCrudOpened = getState().entityCrud.isOpen || false;
                        dispatch(open());
                        if (
                            !wasCrudOpened &&
                            Object.entries(tabs).length === 1 &&
                            !entityCrud.avoidRedirects
                        ) {
                            const currentRoute = getCurrentPath();
                            ensureRoute(`${currentRoute}/edit`);
                        }
                    } else if (!e || (e && e.serverError)) {
                        serverError = true;
                    }

                    dispatch({
                        type: ENTITY_CRUD_SAVE_ERROR,
                        errors: e ? e.errors : e,
                        firstErrorField: e ? e.firstErrorField : e,
                    });
                })
                .finally(() => {
                    if (!saveFromDetail) dispatch({ type: ENTITY_CRUD_SAVE_LOADING, state: false });
                });
        });
}

export function forceUpdateErrors(entity, error) {
    return (dispatch, getState) => {
        dispatch({
            type: ENTITY_CRUD_SAVE_ERROR,
            errors: error.errors,
            firstErrorField: error.firstErrorField,
        });
    };
}

export function updateTableErrors(errors) {
    return (dispatch, getState) => {
        dispatch({
            type: ENTITY_CRUD_UPDATE_TABLE_ERRORS,
            errors: errors,
        });
    };
}

export function open() {
    return (dispatch) => {
        dispatch({ type: ENTITY_CRUD_OPEN });
    };
}

export function close(hasCrudInDetail = false) {
    return (dispatch, getState) => {
        const state = getState();
        const entityCrudActiveName = getActiveCrudName(state);
        const entityDetail = getState().entityDetail[entityCrudActiveName];

        const entityCrudActive = getActiveCrud(state);

        const manager =
            entityCrudActive && Context.entityManager.getEntitiesManager(entityCrudActive);

        const isModal = entityCrudActive.isModal || false;
        if (hasCrudInDetail && !isModal) {
            dispatch({ type: ENTITY_CRUD_HIDE });
        } else {
            dispatch({ type: ENTITY_CRUD_CLOSE, tabs: getState().entityDetail.tabs });
        }

        if (entityDetail || entityCrudActive.isFromTab) {
            // open to prevent the router default action
            dispatch(Context.actions.EntityDetailActions.openHidden());
        }

        if (manager?.afterCrudClose) manager.afterCrudClose();

        dispatch({ type: ENTITY_CRUD_CHECK_DUPLICATE_RESET });

        if (entityCrudActive.avoidRedirects) return;
        if (manager && manager.backRoute) {
            manager.backRoute(entityCrudActive);
        } else {
            ensureRoute(backRoute());
        }
    };
}

export function toggleConfirmModal(bool) {
    return (dispatch) => {
        dispatch({
            type: ENTITY_CRUD_TOGGLE_CONFIRM_MODAL,
            bool,
        });
    };
}

export function toggleExtraFieldsModal(bool, url, queryString) {
    return (dispatch) => {
        dispatch({
            type: ENTITY_CRUD_TOGGLE_EXTRAFIELD_MODAL,
            bool,
            url,
            queryString,
        });
    };
}

export function deleteEntity(entity) {
    return (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const entityCrud = getActiveCrud(getState());
            const isModal = entityCrud.isModal || false;
            const entityList = getState()?.entityList?.[getState()?.entityList?.active] || null;
            const useLazyLoad = entityList?.useLazyLoad || false;
            const manager =
                (entityCrud && Context.entityManager.getEntitiesManager(entityCrud)) || null;
            Context.entityCrudManager
                .delete(entity, entityCrud)
                .then((data) => {
                    if (isModal) return resolve(data);
                    dispatch({ type: ENTITY_DETAIL_CLOSE });
                    dispatch({ type: ENTITY_CRUD_CLOSE, tabs: getState().entityDetail.tabs });
                    dispatch(Context.actions.EntityListSelectActions.resetListSelect(entity));
                    if (manager && manager.afterCrudClose) manager.afterCrudClose();
                    const id = entityCrud.data?.id || '';
                    if (id) trackEvent({ entity, entityId: id, idEventType: EVENT_TYPES.delete });

                    // useLazyLoad for w5 table
                    // make 2 possible calls because null removes default prop values
                    if (manager?.customInitList) {
                        manager.customInitList(entityCrud.data, data?.Insert?.id);
                    } else if (useLazyLoad) {
                        dispatch(
                            Context.actions.EntityListActions.init(
                                entity,
                                false,
                                null,
                                null,
                                null,
                                useLazyLoad,
                            ),
                        );
                    } else {
                        let pageSize = null;
                        if (manager && manager.getCustomPageSize) {
                            pageSize = manager.getCustomPageSize();
                        }
                        dispatch(
                            Context.actions.EntityListActions.init(entity, true, pageSize || null),
                        );
                    }

                    resolve(data);
                    const entityConstant = getEntityFromString(entityCrud.entity);
                    if (!entityConstant.withoutRedirect && !entityCrud.avoidRedirects) {
                        const isMapOpen = window.location.href.includes('/map/');
                        ensureRoute(
                            isMapOpen ? `${entityConstant.route}/map` : entityConstant.route,
                        );
                    }
                })
                .catch((e) => {
                    console.error(e);
                    let serverError = !e || (e && e.serverError);

                    reject({ errors: e.errors, serverError });
                });
        });
}

export function checkDuplicate(entity, field, fieldLabel, value) {
    return (dispatch, getState) => {
        const state = getState();
        const active = getActiveCrudName(state);
        const crud = active && state.entityCrud[active];
        const isEdit = crud && crud.id;
        const duplicateControlMode = String(state?.config?.permission?.checkDuplicateFields ?? '');
        let toastsDuplicate = (crud && crud.duplicates && crud.duplicates.toasts) || {};

        if (isEdit || duplicateControlMode !== '1') return;
        if (toastsDuplicate && toastsDuplicate[field] && toastsDuplicate[field].value === value) {
            return;
        }

        dispatch({ type: ENTITY_CRUD_CHECK_DUPLICATE, currentField: field });

        if (value) {
            Context.domainManager
                .getDuplicateForFields(entity, field, value)
                .then((data) => {
                    if (
                        toastsDuplicate &&
                        toastsDuplicate[field] &&
                        toastsDuplicate[field].value !== value
                    ) {
                        clearToast(toastsDuplicate[field].id);
                    }

                    const translateEntities = {
                        accounts: 'companies',
                        contacts: 'contacts',
                    };

                    let avatar;
                    const newEntity = translateEntities[data.entity];

                    switch (newEntity) {
                        case 'contacts':
                            let initials = '';
                            if (data.name) {
                                const nameSplitted = data.name.toUpperCase().split(' ');
                                initials = `${nameSplitted[0][0]}${nameSplitted[1][0]}`;
                            }
                            avatar = (
                                <ContactAvatar size="large" id={data.id} initials={initials} />
                            );
                            break;
                        case 'companies':
                        default:
                            // we are using CompanyAvatar component which is using the old format of backend to
                            // get the image of the company. In some cases, backend is not returning the image
                            // of the company and you need to obtain an image with the idLogo property
                            // of the company
                            avatar = <CompanyAvatar size="large" id={data.id} />;
                            break;
                    }

                    const toastId = duplicatesToast({
                        title: `${getLiteral('label_duplicate_detected')} - ${fieldLabel}`,
                        icon: avatar,
                        content: (
                            <CheckDuplicateContent
                                entity={data.entity}
                                id={data.id}
                                name={data.name}
                                visible={data.visible}
                                fieldLabel={fieldLabel}
                                toastField={field}
                            />
                        ),
                        onClose: () =>
                            Context.store.dispatch(
                                Context.actions.EntityCrudActions.closeDuplicateToastFromButton(
                                    field,
                                ),
                            ),
                        className: 'check-duplicate-toast',
                    });

                    dispatch({
                        type: ENTITY_CRUD_CHECK_DUPLICATE_SUCCESS,
                        toastsDuplicate,
                        fieldToRemove: field,
                        newField: field,
                        newValues: {
                            id: toastId,
                            value: value,
                        },
                    });
                })
                .catch((error) => {
                    dispatch({ type: ENTITY_CRUD_CHECK_DUPLICATE_ERROR });
                });
        } else {
            if (toastsDuplicate[field]) {
                clearToast(toastsDuplicate[field].id);
                dispatch({
                    type: ENTITY_CRUD_CHECK_DUPLICATE_SUCCESS,
                    toastsDuplicate,
                    fieldToRemove: field,
                });
            } else {
                dispatch({ type: ENTITY_CRUD_CHECK_DUPLICATE_SUCCESS });
            }
        }
    };
}

export function closeDuplicateToastFromButton(toastField) {
    return (dispatch, getState) => {
        const activeCrud = getActiveCrud(getState());
        let toastsDuplicate =
            (activeCrud && activeCrud?.duplicates && activeCrud?.duplicates?.toasts) || {};

        dispatch({
            type: ENTITY_CRUD_CHECK_DUPLICATE_SUCCESS,
            toastsDuplicate,
            fieldToRemove: toastField,
        });
    };
}

export function toggleDuplicatesOverlay(bool) {
    return (dispatch, getState) => {
        const activeCrud = getActiveCrud(getState());
        const entity = activeCrud?.entity;
        dispatch({
            type: ENTITY_CRUD_OVERLAY,
            entity,
            bool,
        });
    };
}

export function setDuplicatesError() {
    return (dispatch, getState) => {
        const activeCrud = getActiveCrud(getState());
        const entity = activeCrud?.entity;
        dispatch({
            type: ENTITY_CRUD_DUPLICATES_ERROR,
            entity,
        });
    };
}

export function setActiveCrud(active) {
    return (dispatch) => {
        dispatch({
            type: ENTITY_CRUD_SET_ACTIVE,
            active,
        });
    };
}

export function setFieldsToClear(fieldsToClear) {
    return (dispatch) => {
        dispatch({
            type: ENTITY_CRUD_SET_FIELDS_TO_CLEAR,
            fieldsToClear,
        });
    };
}
